import { useState } from "react";
import { money, fileUrl, uploadDoc } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge, EmptyState } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { ScrollText, FileText, Check, Trash2, Repeat, DollarSign, Bell, CheckCircle2, UserMinus, AlertTriangle, Plus, X, RotateCcw, ClipboardCheck, FilePlus, Paperclip, Loader2, Image } from "lucide-react";

const PURGE_DAYS = 30;
const daysSince = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : 0);

const centralNow = () => new Date(new Date().toLocaleString("en-US", { timeZone: "America/Chicago" }));
const currentRentPeriod = () => { const c = centralNow(); return `${c.getFullYear()}-${String(c.getMonth() + 1).padStart(2, "0")}`; };
const currentMonthLabel = () => centralNow().toLocaleDateString("en-US", { month: "long", year: "numeric" });

const METHODS = [["venmo", "Venmo"], ["cash", "Cash"], ["check", "Check"], ["zelle", "Zelle"], ["paypal", "PayPal"], ["other", "Other"]];

const MarkPaidButton = ({ lease, markRentPaid }) => {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState("venmo");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    const ok = await markRentPaid(lease.id, method, note.trim());
    setBusy(false);
    if (ok) { setOpen(false); setNote(""); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`mark-rent-paid-${lease.id}`} className="border border-forest text-forest px-3 py-1.5 rounded-lg text-sm hover:bg-forest/5 transition-colors flex items-center gap-1.5"><DollarSign className="h-4 w-4" /> Mark rent as paid</button>
      </DialogTrigger>
      <DialogContent data-testid={`mark-rent-dialog-${lease.id}`}>
        <DialogHeader>
          <DialogTitle>Record {currentMonthLabel()} rent</DialogTitle>
          <DialogDescription>Record a rent payment you received outside the app (e.g. Venmo, cash, check) for {lease.property_title} — {money(lease.monthly_rent)}. The tenant will be notified.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <p className="text-xs font-medium text-ink mb-1.5">Payment method</p>
            <div className="flex flex-wrap gap-1.5">
              {METHODS.map(([val, label]) => (
                <button key={val} data-testid={`mark-rent-method-${val}`} onClick={() => setMethod(val)} className={`text-sm rounded-full px-3 py-1 border transition-colors ${method === val ? "border-forest bg-forest/10 text-forest" : "border-line text-ink hover:bg-bone"}`}>{label}</button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-ink mb-1">Note <span className="text-mute font-normal">(optional)</span></p>
            <input data-testid={`mark-rent-note-${lease.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Venmo transfer #1234" className="w-full border border-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest/20" />
          </div>
        </div>
        <DialogFooter>
          <button data-testid={`mark-rent-confirm-${lease.id}`} disabled={busy} onClick={submit} className="bg-forest text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-forest-hover disabled:opacity-60">{busy ? "Recording…" : "Record payment"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const FIXED_CHECKLIST = ["Keys returned", "Deposit settled", "Final rent collected", "Property inspected"];

const RemoveTenantDialog = ({ lease, endLease, deleteLease }) => {
  const [open, setOpen] = useState(false);
  const [moveOut, setMoveOut] = useState("");
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [ackDelete, setAckDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deposit, setDeposit] = useState(lease.security_deposit != null && lease.security_deposit !== 0 ? String(lease.security_deposit) : "");
  const [depositNote, setDepositNote] = useState("");
  const [checklist, setChecklist] = useState(FIXED_CHECKLIST.map((label) => ({ label, done: false })));
  const [customLabel, setCustomLabel] = useState("");
  const [warned, setWarned] = useState(false);
  const isEnded = lease.status === "ended";
  const allDone = checklist.every((c) => c.done);

  const reset = () => { setAckDelete(false); setWarned(false); };
  const toggleItem = (i) => setChecklist((cl) => cl.map((it, idx) => (idx === i ? { ...it, done: !it.done } : it)));
  const removeItem = (i) => setChecklist((cl) => cl.filter((_, idx) => idx !== i));
  const addCustom = () => {
    const label = customLabel.trim();
    if (!label) return;
    setChecklist((cl) => [...cl, { label, done: false }]);
    setCustomLabel("");
  };

  const endPayload = () => ({
    move_out_date: moveOut,
    reason: reason.trim(),
    notify,
    deposit_returned: deposit === "" ? null : parseFloat(deposit),
    deposit_note: depositNote.trim(),
    checklist,
  });

  const doEnd = async () => {
    if (!allDone && !warned) { setWarned(true); return; }
    setBusy(true);
    const ok = await endLease(lease.id, endPayload());
    setBusy(false);
    if (ok) { setOpen(false); reset(); }
  };
  const doDelete = async () => {
    setBusy(true);
    const ok = await deleteLease(lease.id, { move_out_date: moveOut, reason: reason.trim(), notify });
    setBusy(false);
    if (ok) { setOpen(false); reset(); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <button data-testid={`remove-tenant-${lease.id}`} className="border border-terracotta/40 text-terracotta px-3 py-1.5 rounded-lg text-sm hover:bg-terracotta/5 transition-colors flex items-center gap-1.5">
          <UserMinus className="h-4 w-4" /> {isEnded ? "Delete lease" : "Remove tenant"}
        </button>
      </DialogTrigger>
      <DialogContent data-testid={`remove-tenant-dialog-${lease.id}`} className="max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEnded ? "Delete this lease?" : "Remove tenant / end lease"}</DialogTitle>
          <DialogDescription>
            {isEnded
              ? `This lease for ${lease.property_title} is already ended. You can permanently delete the record below.`
              : `End the tenancy for ${lease.renter_name} at ${lease.property_title} (e.g. they moved out early). The listing goes off-market until you re-list it, and ${lease.renter_name}'s account is unaffected — they can still apply to other homes.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!isEnded && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-medium text-ink mb-1">Move-out date <span className="text-mute font-normal">(optional)</span></p>
                  <input type="date" data-testid={`remove-moveout-${lease.id}`} value={moveOut} onChange={(e) => setMoveOut(e.target.value)} className="w-full border border-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest/20" />
                </div>
                <div>
                  <p className="text-xs font-medium text-ink mb-1">Reason <span className="text-mute font-normal">(optional)</span></p>
                  <input data-testid={`remove-reason-${lease.id}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Relocated for work" className="w-full border border-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest/20" />
                </div>
              </div>

              {/* Security deposit return */}
              <div className="rounded-lg border border-line p-3 space-y-2">
                <p className="text-xs font-semibold text-ink flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5 text-forest" /> Security deposit return</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-mute mb-1">Amount returned</p>
                    <input type="number" min="0" step="0.01" data-testid={`deposit-returned-${lease.id}`} value={deposit} onChange={(e) => setDeposit(e.target.value)} placeholder="0.00" className="w-full border border-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest/20" />
                    {lease.security_deposit ? <p className="text-[11px] text-mute mt-1">Deposit on file: {money(lease.security_deposit)}</p> : null}
                  </div>
                  <div>
                    <p className="text-xs text-mute mb-1">Note <span className="font-normal">(optional)</span></p>
                    <input data-testid={`deposit-note-${lease.id}`} value={depositNote} onChange={(e) => setDepositNote(e.target.value)} placeholder="e.g. $150 deducted for cleaning" className="w-full border border-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest/20" />
                  </div>
                </div>
              </div>

              {/* Move-out checklist */}
              <div className="rounded-lg border border-line p-3 space-y-2">
                <p className="text-xs font-semibold text-ink flex items-center gap-1.5"><ClipboardCheck className="h-3.5 w-3.5 text-forest" /> Move-out checklist</p>
                <div className="space-y-1.5">
                  {checklist.map((it, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <label className="flex items-center gap-2 text-sm text-ink cursor-pointer flex-1">
                        <input type="checkbox" data-testid={`checklist-item-${lease.id}-${i}`} checked={it.done} onChange={() => toggleItem(i)} className="h-4 w-4 accent-forest" />
                        <span className={it.done ? "line-through text-mute" : ""}>{it.label}</span>
                      </label>
                      {i >= FIXED_CHECKLIST.length && (
                        <button type="button" data-testid={`checklist-remove-${lease.id}-${i}`} onClick={() => removeItem(i)} className="text-mute hover:text-terracotta"><X className="h-3.5 w-3.5" /></button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <input data-testid={`checklist-custom-input-${lease.id}`} value={customLabel} onChange={(e) => setCustomLabel(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }} placeholder="Add a custom item…" className="flex-1 border border-line rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-forest/20" />
                  <button type="button" data-testid={`checklist-add-${lease.id}`} onClick={addCustom} className="border border-line text-ink rounded-lg px-2.5 py-1.5 text-sm hover:bg-bone flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add</button>
                </div>
              </div>

              {warned && !allDone && (
                <p data-testid={`checklist-warn-${lease.id}`} className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> Some checklist items aren't marked done. You can still end the lease — click "End anyway" to confirm.
                </p>
              )}
            </>
          )}
          <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
            <input type="checkbox" data-testid={`remove-notify-${lease.id}`} checked={notify} onChange={(e) => setNotify(e.target.checked)} className="h-4 w-4 accent-forest" />
            Notify {lease.renter_name} by email & in-app
          </label>
          <label className="flex items-start gap-2 text-sm text-ink cursor-pointer bg-terracotta/5 border border-terracotta/20 rounded-lg p-2.5">
            <input type="checkbox" data-testid={`remove-ack-${lease.id}`} checked={ackDelete} onChange={(e) => setAckDelete(e.target.checked)} className="h-4 w-4 mt-0.5 accent-terracotta" />
            <span className="flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5 text-terracotta shrink-0" /> I understand permanent delete removes this lease and its signed documents for good.</span>
          </label>
        </div>
        <DialogFooter className="gap-2">
          {!isEnded && (
            <button data-testid={`confirm-end-lease-${lease.id}`} disabled={busy} onClick={doEnd} className="border border-forest text-forest px-4 py-2 rounded-lg text-sm font-medium hover:bg-forest/5 disabled:opacity-60">{busy ? "Working…" : (warned && !allDone ? "End anyway" : "End lease (keep record)")}</button>
          )}
          <button data-testid={`confirm-delete-lease-${lease.id}`} disabled={busy || !ackDelete} onClick={doDelete} className="bg-terracotta text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-terracotta-hover disabled:opacity-40">{busy ? "Working…" : "Delete permanently"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AddDocumentsDialog = ({ lease, addLeaseDocuments }) => {
  const [open, setOpen] = useState(false);
  const [leaseDocs, setLeaseDocs] = useState([]);   // {name, path}
  const [infoDocs, setInfoDocs] = useState([]);     // {name, path}
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const addFiles = async (fileList, kind) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      try {
        const res = await uploadDoc(file);
        const ref = { name: res.name, path: res.path };
        if (kind === "lease") setLeaseDocs((p) => [...p, ref]);
        else setInfoDocs((p) => [...p, ref]);
      } catch (e) {
        toast.error(e.response?.data?.detail || `Could not upload ${file.name}`);
      }
    }
    setUploading(false);
  };

  const removeLease = (i) => setLeaseDocs((p) => p.filter((_, idx) => idx !== i));
  const removeInfo = (i) => setInfoDocs((p) => p.filter((_, idx) => idx !== i));
  const reset = () => { setLeaseDocs([]); setInfoDocs([]); };

  const submit = async () => {
    if (!leaseDocs.length && !infoDocs.length) { toast.error("Add at least one document first"); return; }
    setBusy(true);
    const ok = await addLeaseDocuments(lease.id, { lease_docs: leaseDocs, info_docs: infoDocs });
    setBusy(false);
    if (ok) { setOpen(false); reset(); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <button data-testid={`add-docs-${lease.id}`} className="border border-line text-ink px-3 py-1.5 rounded-lg text-sm hover:bg-bone transition-colors flex items-center gap-1.5"><FilePlus className="h-4 w-4 text-forest" /> Add documents</button>
      </DialogTrigger>
      <DialogContent data-testid={`add-docs-dialog-${lease.id}`} className="max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add documents</DialogTitle>
          <DialogDescription>Upload a lease agreement/addendum or supporting documents for {lease.property_title}. These are stored privately for your records (your tenant can't see them).</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Lease agreements / addenda (PDF) */}
          <div className="rounded-lg border border-line p-3 space-y-2">
            <p className="text-xs font-semibold text-ink flex items-center gap-1.5"><ScrollText className="h-3.5 w-3.5 text-forest" /> Lease agreement / addendum <span className="text-mute font-normal">(PDF)</span></p>
            <label className="flex items-center gap-2 text-sm text-ink cursor-pointer border border-line border-dashed rounded-lg px-3 py-2 hover:bg-bone w-fit">
              <Paperclip className="h-4 w-4 text-forest" /> {uploading ? "Uploading…" : "Upload PDF"}
              <input data-testid={`add-docs-lease-input-${lease.id}`} type="file" accept="application/pdf" multiple className="hidden" onChange={(e) => { addFiles(e.target.files, "lease"); e.target.value = ""; }} />
            </label>
            {leaseDocs.map((d, i) => (
              <div key={d.path} className="flex items-center gap-2 text-sm">
                <FileText className="h-3.5 w-3.5 text-mute" /><span className="text-ink flex-1 truncate">{d.name}</span>
                <button type="button" onClick={() => removeLease(i)} className="text-mute hover:text-terracotta"><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
          {/* Supporting documents (PDF or image) */}
          <div className="rounded-lg border border-line p-3 space-y-2">
            <p className="text-xs font-semibold text-ink flex items-center gap-1.5"><Image className="h-3.5 w-3.5 text-forest" /> Supporting documents <span className="text-mute font-normal">(PDF or image)</span></p>
            <label className="flex items-center gap-2 text-sm text-ink cursor-pointer border border-line border-dashed rounded-lg px-3 py-2 hover:bg-bone w-fit">
              <Paperclip className="h-4 w-4 text-forest" /> {uploading ? "Uploading…" : "Upload PDF or image"}
              <input data-testid={`add-docs-info-input-${lease.id}`} type="file" accept="application/pdf,image/jpeg,image/png" multiple className="hidden" onChange={(e) => { addFiles(e.target.files, "info"); e.target.value = ""; }} />
            </label>
            {infoDocs.map((d, i) => (
              <div key={d.path} className="flex items-center gap-2 text-sm">
                <FileText className="h-3.5 w-3.5 text-mute" /><span className="text-ink flex-1 truncate">{d.name}</span>
                <button type="button" onClick={() => removeInfo(i)} className="text-mute hover:text-terracotta"><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <button data-testid={`add-docs-submit-${lease.id}`} disabled={busy || uploading} onClick={submit} className="bg-forest text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-forest-hover disabled:opacity-60 inline-flex items-center gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FilePlus className="h-4 w-4" />} Save to lease</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const LeasesTab = ({ leases, sendInvoice, sendReminder, deleteSignedLeaseDoc, markRentPaid, endLease, deleteLease, relistUnit, addLeaseDocuments, deleteLeaseInfoDoc }) => {
  if (leases.length === 0) {
    return <div className="bg-white border border-line rounded-xl"><EmptyState icon={ScrollText} title="No leases yet" subtitle="Approve an application, then create a lease to send for e-signature." /></div>;
  }
  return (
    <div className="space-y-3">
      {leases.map((l) => (
        <div key={l.id} className="bg-white border border-line rounded-xl p-5 flex flex-wrap gap-4 items-center justify-between">
          <div>
            <div className="flex items-center gap-2"><p className="font-medium text-ink">{l.property_title}</p><StatusBadge status={l.status} />{l.deposit_paid && <StatusBadge status="completed" />}{l.autopay_enabled && <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold border bg-forest/5 text-forest border-forest/20"><Repeat className="h-3 w-3" /> Auto-Pay</span>}</div>
            <p className="text-sm text-mute mt-0.5">Tenant {l.renter_name} · {money(l.monthly_rent)}/mo · {l.start_date} → {l.end_date}</p>
            {l.status === "ended" && (
              <p data-testid={`lease-ended-${l.id}`} className="text-sm mt-1 inline-flex items-center gap-1.5 text-terracotta bg-terracotta/5 border border-terracotta/20 rounded-lg px-2.5 py-1">
                <UserMinus className="h-3.5 w-3.5" /> Lease ended{l.move_out_date ? ` · moved out ${l.move_out_date}` : ""}{l.end_reason ? ` · ${l.end_reason}` : ""}
              </p>
            )}
            {l.status === "ended" && (l.deposit_returned != null || l.deposit_note) && (
              <p data-testid={`deposit-return-${l.id}`} className="text-sm mt-1 inline-flex items-center gap-1.5 text-ink bg-bone border border-line rounded-lg px-2.5 py-1">
                <DollarSign className="h-3.5 w-3.5 text-forest" /> Deposit returned{l.deposit_returned != null ? `: ${money(l.deposit_returned)}` : ""}{l.deposit_note ? ` · ${l.deposit_note}` : ""}
              </p>
            )}
            {l.status === "ended" && (l.move_out_checklist || []).length > 0 && (
              <div data-testid={`checklist-summary-${l.id}`} className="mt-1.5 flex flex-wrap gap-1.5">
                {l.move_out_checklist.map((c, i) => (
                  <span key={i} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs border ${c.done ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-mute bg-bone border-line"}`}>
                    {c.done ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />} {c.label}
                  </span>
                ))}
              </div>
            )}
            {l.status === "signed" && l.rent_this_period && (
              <p data-testid={`rent-owed-${l.id}`} className="text-sm mt-1">
                {l.rent_this_period.fully_paid ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700 font-medium"><CheckCircle2 className="h-3.5 w-3.5" /> {currentMonthLabel()} rent fully paid ({money(l.rent_this_period.paid)})</span>
                ) : l.rent_this_period.paid > 0 ? (
                  <span className="text-ink">{currentMonthLabel()}: <span className="font-medium text-forest">{money(l.rent_this_period.paid)}</span> paid · <span className="font-semibold text-terracotta">{money(l.rent_this_period.remaining)} still owed</span></span>
                ) : (
                  <span className="text-mute">{currentMonthLabel()}: <span className="font-semibold text-ink">{money(l.rent_this_period.remaining)}</span> owed · nothing paid yet</span>
                )}
              </p>
            )}
            {l.document && <a data-testid={`ll-doc-${l.id}`} href={l.document} download={l.document_name || "lease"} className="text-sm text-forest mt-1 inline-flex items-center gap-1 hover:underline"><FileText className="h-3.5 w-3.5" /> {l.document_name || "Lease document"}</a>}
            {(l.documents || []).length > 0 && (
              <div className="mt-2 space-y-1">
                <p data-testid={`ll-doc-summary-${l.id}`} className="text-sm font-medium text-ink">
                  {l.documents.filter((d) => d.signed).length} of {l.documents.length} documents signed
                </p>
                {l.documents.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 text-sm flex-wrap">
                    {d.signed ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <FileText className="h-3.5 w-3.5 text-mute" />}
                    <span className="text-ink">{d.name}</span>
                    {d.purged ? (
                      <span className="text-xs text-mute inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> downloaded & removed</span>
                    ) : d.signed ? (
                      <>
                        <a data-testid={`ll-signed-doc-${d.id}`} href={fileUrl(d.signed_path)} target="_blank" rel="noreferrer" className="text-forest hover:underline">Download / Print</a>
                        <button data-testid={`ll-delete-doc-${d.id}`} onClick={() => deleteSignedLeaseDoc(l.id, d.id)} className="text-red-600 hover:underline inline-flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                        {d.signed_at && daysSince(d.signed_at) >= PURGE_DAYS && <span data-testid={`ll-purge-nudge-${d.id}`} className="text-[11px] text-amber-700 font-semibold">stored {daysSince(d.signed_at)}d — please remove</span>}
                      </>
                    ) : (
                      <span className="text-mute text-xs">awaiting signature</span>
                    )}
                  </div>
                ))}
              </div>
            )}
            {(l.info_docs || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {l.info_docs.map((d) => (
                  <span key={d.id || d.path} className="text-xs border border-line rounded-full pl-2.5 pr-1.5 py-1 text-ink inline-flex items-center gap-1 bg-white">
                    <a data-testid={`ll-info-doc-${d.id || "x"}`} href={fileUrl(d.path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline"><FileText className="h-3 w-3" /> {d.name}</a>
                    {d.id && deleteLeaseInfoDoc && (
                      <button data-testid={`ll-info-doc-del-${d.id}`} onClick={() => deleteLeaseInfoDoc(l.id, d.id)} className="text-mute hover:text-terracotta"><X className="h-3 w-3" /></button>
                    )}
                  </span>
                ))}
              </div>
            )}
            {l.signature && !(l.documents || []).length && <p className="text-sm text-emerald-700 mt-1">Signed by {l.signature}</p>}
            {l.signed_pdf && <a data-testid={`ll-signed-pdf-${l.id}`} href={l.signed_pdf} download={l.signed_pdf_name || "signed-lease.pdf"} className="mt-2 inline-flex items-center gap-1.5 bg-forest text-white px-3 py-1.5 rounded-lg text-sm hover:bg-forest-hover transition-colors"><ScrollText className="h-3.5 w-3.5" /> Download Signed PDF</a>}
            {addLeaseDocuments && (
              <div className="mt-3"><AddDocumentsDialog lease={l} addLeaseDocuments={addLeaseDocuments} /></div>
            )}
          </div>
          {l.status === "signed" && (
            <div className="flex flex-col gap-2 items-end">
              {!l.invoice_sent ? (
                <button data-testid={`send-invoice-${l.id}`} onClick={() => sendInvoice(l.id)} className="bg-forest text-white px-3 py-1.5 rounded-lg text-sm hover:bg-forest-hover transition-colors flex items-center gap-1.5"><DollarSign className="h-4 w-4" /> Send move-in invoice</button>
              ) : (
                <span data-testid={`invoice-status-${l.id}`} className={`text-xs font-semibold rounded-full px-2.5 py-0.5 border ${l.invoice_paid ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}>{l.invoice_paid ? `Invoice paid · $${Number(l.invoice_total || 0).toLocaleString()}` : `Invoice sent · $${Number(l.invoice_total || 0).toLocaleString()}`}</span>
              )}
              <button data-testid={`remind-${l.id}`} onClick={() => sendReminder(l.id)} className="border border-line text-ink px-3 py-1.5 rounded-lg text-sm hover:bg-bone transition-colors flex items-center gap-1.5"><Bell className="h-4 w-4 text-forest" /> Send Rent Reminder</button>
              <MarkPaidButton lease={l} markRentPaid={markRentPaid} />
              {l.last_rent_paid_period === currentRentPeriod() && (
                <span data-testid={`rent-paid-chip-${l.id}`} className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> {currentMonthLabel()} rent recorded</span>
              )}
              <RemoveTenantDialog lease={l} endLease={endLease} deleteLease={deleteLease} />
            </div>
          )}
          {l.status === "ended" && (
            <div className="flex flex-col gap-2 items-end">
              {l.property_id && relistUnit && (
                <button data-testid={`relist-unit-${l.id}`} onClick={() => relistUnit(l.property_id)} className="bg-forest text-white px-3 py-1.5 rounded-lg text-sm hover:bg-forest-hover transition-colors flex items-center gap-1.5"><RotateCcw className="h-4 w-4" /> Re-list this unit</button>
              )}
              <RemoveTenantDialog lease={l} endLease={endLease} deleteLease={deleteLease} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
